import type { MunicipalityCollaborator } from "@/api/municipality_collaborators";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const UNASSIGNED = "unassigned";

// The milestone owner picker (ENG-1570), shared by the confirmed
// opportunity's milestones UI and the planning modal's review editor
// (ENG-1981) so both surfaces assign through the same model: a collaborator
// id or no owner, never free text.
type MilestoneOwnerSelectProps = {
  id?: string;
  ariaLabel: string;
  value: number | null;
  onChange: (ownerId: number | null) => void;
  collaborators: MunicipalityCollaborator[];
  loading: boolean;
  disabled?: boolean;
  triggerClassName?: string;
};

export function MilestoneOwnerSelect({
  id,
  ariaLabel,
  value,
  onChange,
  collaborators,
  loading,
  disabled = false,
  triggerClassName,
}: MilestoneOwnerSelectProps) {
  return (
    <Select
      value={value == null ? UNASSIGNED : String(value)}
      onValueChange={(next) => onChange(next === UNASSIGNED ? null : Number(next))}
      disabled={loading || disabled}
    >
      <SelectTrigger id={id} className={triggerClassName} aria-label={ariaLabel}>
        <SelectValue placeholder={loading ? "Loading team…" : "No owner"} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={UNASSIGNED}>No owner</SelectItem>
        {collaborators.map((collaborator) => (
          <SelectItem key={collaborator.id} value={String(collaborator.id)}>
            {collaborator.email}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// A milestone can hold an owner who has since left the collaborator list
// (or falls outside a department scope); the select must still show them
// rather than silently rendering the first option.
export function ownerSelectOptions(
  ownerId: number | null,
  ownerName: string | null,
  collaborators: MunicipalityCollaborator[],
): MunicipalityCollaborator[] {
  if (ownerId == null) {
    return collaborators;
  }
  if (collaborators.some((collaborator) => collaborator.id === ownerId)) {
    return collaborators;
  }
  return [...collaborators, { id: ownerId, email: ownerName ?? `User #${ownerId}` }];
}
